import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    EventEmitter,
    inject,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    signal,
    viewChild,
    ElementRef,
    effect,
} from '@angular/core';
import { Router, NavigationEnd, RouterLink } from '@angular/router';
import { navigation } from '../../../config/navigationLinks.const';
import { NavigationLinkCategory } from '../../../models/navigation-link';
import { AuthService } from '../../auth/auth.service';
import { HeaderModeService } from '../../header-mode/header-mode.service';
import { debounce } from '@co-3/ctrl-ui/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';
import { ClickOutsideDirective } from '@co-3/ctrl-ui/util';
import { CtrlIconComponent } from '@co-3/ctrl-ui/icons';

@Component({
    selector: 'app-header',
    imports: [RouterLink, NgClass, ClickOutsideDirective, CtrlIconComponent],
    templateUrl: './header.component.html',
    styleUrl: './header.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { '(keydown.esc)': 'closeAllNavMenus()' },
})
export class HeaderComponent implements OnInit, OnDestroy {
    private authService = inject(AuthService);
    private headerModeService = inject(HeaderModeService);
    private changeDetectorRef = inject(ChangeDetectorRef);
    private router = inject(Router);
    private zone = inject(NgZone);
    @Output()
    public mainMenuToggle = new EventEmitter<boolean>();

    public isLoggedIn: boolean;
    public navigation: NavigationLinkCategory[] = navigation.sort((a, b) => (a.index > b.index ? 1 : -1));
    public detached: boolean = false;

    protected headerMode = toSignal(this.headerModeService.headerMode$);
    protected isMainNavMenuOpen = signal(false);
    protected isSubNavMenuOpen = signal(navigation.map(() => false));
    protected isAnySubNavOpen = computed(() => (this.isSubNavMenuOpen().find((e) => e === true) ? true : false));

    private intersectionObserver: IntersectionObserver;
    private resizeObserver: ResizeObserver;

    private hamburgerButton = viewChild<ElementRef<HTMLButtonElement>>('hamburgerButton');

    public ngOnInit(): void {
        this.subscribeForRouterEvents();
        this.observeScrollBehavior();
        this.subscribeForLoginEvents();
    }

    public ngOnDestroy(): void {
        this.intersectionObserver?.disconnect();
        this.resizeObserver?.disconnect();
    }

    public constructor() {
        effect(() => {
            const button = this.hamburgerButton();
            if (button) {
                this.closeHamburgerMenuWhenHidden(button.nativeElement);
            }
        });
    }

    private closeHamburgerMenuWhenHidden(button: HTMLElement): void {
        this.resizeObserver?.disconnect();

        this.resizeObserver = new ResizeObserver((entries) => {
            const entry = entries[0];
            const hamburgerButton = entry.target as HTMLElement;
            const isVisible = hamburgerButton.offsetWidth > 0;

            if (!isVisible && this.isMainNavMenuOpen()) {
                this.closeAllNavMenus();
            }
        });

        this.resizeObserver.observe(button);
    }

    protected getNavigationId(index: number): string {
        return `navigation-${index}`;
    }

    protected getMenuId(index: number): string {
        return `menu-for-navigation-${index}`;
    }

    protected getNavigationItemId(index: number, subIndex: number): string {
        return `drop-${index}-${subIndex}`;
    }

    protected onNavigationKeydown(event: KeyboardEvent, linkGroup: NavigationLinkCategory, index: number): void {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (linkGroup.links.length > 0) {
                linkGroup.open = true;
                this.focusOnFirstItem(index);
            }
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            if (linkGroup.links.length > 0) {
                linkGroup.open = true;
                this.focusOnFirstItem(index);
            }
        }
        if (event.key === 'Escape') {
            linkGroup.open = false;
        }
    }

    protected onNavigationItemKeydown(
        event: KeyboardEvent,
        linkGroup: NavigationLinkCategory,
        index: number,
        subIndex: number,
    ): void {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            const id = this.getNavigationItemId(index, subIndex + 1);
            const nextItem = document.getElementById(id);
            if (nextItem) {
                nextItem.focus();
            } else {
                this.focusOnFirstItem(index);
            }
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            const id = this.getNavigationItemId(index, subIndex - 1);
            const nextItem = document.getElementById(id);
            if (nextItem) {
                nextItem.focus();
            } else {
                this.focusOnLastItem(linkGroup, index);
            }
        }

        if (event.key === 'Home') {
            event.preventDefault();
            this.focusOnFirstItem(index);
        }

        if (event.key === 'End') {
            event.preventDefault();
            this.focusOnLastItem(linkGroup, index);
        }

        if (event.key === 'Escape') {
            linkGroup.open = false;
            const id = this.getNavigationId(index);
            const nextItem = document.getElementById(id);
            if (nextItem) {
                nextItem.focus();
            }
        }

        if (event.key === 'Tab') {
            linkGroup.open = false;
        }
        if (event.key === 'Enter') {
            linkGroup.open = false;
        }
    }

    protected closeAllNavMenus(): void {
        this.isMainNavMenuOpen.set(false);
        this.isSubNavMenuOpen.set(this.isSubNavMenuOpen().map(() => false));
        this.mainMenuToggle.emit(this.isMainNavMenuOpen());
        this.navigation.forEach((linkGroup) => (linkGroup.open = false));
    }

    protected toggleMainNavMenuOpen(): void {
        this.isMainNavMenuOpen.set(!this.isMainNavMenuOpen());
        this.mainMenuToggle.emit(this.isMainNavMenuOpen());
    }

    protected toggleSubNavMenuOpen(index: number): void {
        const isSubNavigationOpen = [...this.isSubNavMenuOpen()];
        isSubNavigationOpen[index] = !isSubNavigationOpen[index];
        this.isSubNavMenuOpen.set([...isSubNavigationOpen]);
    }

    private subscribeForRouterEvents(): void {
        this.router.events.subscribe((e) => {
            if (e instanceof NavigationEnd) {
                // close nav menu on successful navigation:
                const drops = document.querySelectorAll('[id^="drop"]') as NodeListOf<HTMLInputElement>;
                drops.forEach((drop) => {
                    drop.checked = false;
                });
            }
        });
    }

    private subscribeForLoginEvents(): void {
        this.authService.isLoggedIn$.subscribe((val) => {
            this.isLoggedIn = val;
            this.changeDetectorRef.detectChanges();
        });
    }

    private observeScrollBehavior(): void {
        const marker = document.getElementById('header-scroll-effect-marker');
        if (!marker) {
            return;
        }
        // On Chrome, the scroll behavior is handled differently from firefox, if
        // you only scroll down a short amount, resizing of the header will move
        // the scroll position back to the top. This was causing a visible stutter
        // effect when scrolling slowly. Therefore we're using a debounce to ensure
        // the detaching callback is only called once.
        this.zone.runOutsideAngular(() => {
            this.intersectionObserver = new IntersectionObserver(
                debounce(
                    (entries: IntersectionObserverEntry[]) => {
                        const entry = entries.find((e) => e.target === marker);

                        if (entry) {
                            const { isIntersecting } = entry;

                            this.detached = !isIntersecting;
                            this.changeDetectorRef.detectChanges();
                        }
                    },
                    100,
                    { leading: true, trailing: false },
                ),
            );
            this.intersectionObserver.observe(marker);
        });
    }

    private focusOnFirstItem(index: number): void {
        const id = this.getNavigationItemId(index, 0);

        // We need to delay setting focus, to ensure the dropdown is open
        setTimeout(() => {
            const nextItem = document.getElementById(id);
            if (nextItem) {
                nextItem.focus();
            }
        }, 1);
    }

    private focusOnLastItem(linkGroup: NavigationLinkCategory, index: number): void {
        const id = this.getNavigationItemId(index, linkGroup.links.length - 1);
        setTimeout(() => {
            const nextItem = document.getElementById(id);
            if (nextItem) {
                nextItem.focus();
            }
        }, 1);
    }
}
